import { Head } from "@inertiajs/react"
import { Check, Clock, Users, Code, MessageSquare, Zap, Calendar, GitBranch } from "lucide-react"
import { CompanyLayout } from "~/components/company/CompanyLayout"
import { ServicePageCTA } from "~/components/company/ServicePageCTA"
import { ContactSection } from "~/components/company/sections/ContactSection"
import { HeroTeamVelocity } from "~/components/company/sections/HeroTeamVelocity"
import { SectionReveal, StaggerReveal, StaggerItem, FadeScale } from "~/components/company/SectionReveal"

export default function DedicatedDevelopersPage() {
  return (
    <CompanyLayout>
      <Head>
        <title>Dedicated Developers | Optimal Dev Labs</title>
        <meta name="description" content="Scale your engineering team without hiring. Senior full-stack developers who integrate with your team and ship fast. Month-to-month retainers from $8k/mo. No long-term contracts." />
        <meta name="keywords" content="dedicated developers, dedicated development team, staff augmentation, hire senior developers, scale engineering team, fractional cto, outsourced development team, senior full stack developer" />
        <meta property="og:title" content="Dedicated Developers | Optimal Dev Labs" />
        <meta property="og:description" content="Scale your engineering team without hiring. Senior full-stack developers, month-to-month retainers from $8k/mo." />
        <meta property="og:url" content="https://optimaldevlabs.com/services/dedicated-developers" />
        <meta name="twitter:title" content="Dedicated Developers | Optimal Dev Labs" />
        <meta name="twitter:description" content="Scale your engineering team without hiring. Month-to-month retainers from $8k/mo. No long-term contracts." />
        <link rel="canonical" href="https://optimaldevlabs.com/services/dedicated-developers" />
      </Head>
      <main className="flex-1">
        <section className="w-full bg-white py-12 md:py-20 lg:py-28">
          <div className="container px-4 md:px-6 lg:px-8">
            <div className="flex flex-col lg:flex-row gap-8 lg:gap-16 items-center max-w-6xl mx-auto">
              <SectionReveal className="flex flex-col space-y-6 text-center lg:text-left lg:flex-1">
                <div className="space-y-4">
                  <p className="text-[#ed2132] font-semibold text-sm">DEDICATED DEVELOPERS</p>
                  <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 leading-tight tracking-tight text-balance">
                    Scale Your Team Without Hiring
                  </h1>
                  <p className="text-lg sm:text-xl md:text-2xl text-slate-600 max-w-xl mx-auto lg:mx-0 text-balance">
                    Senior developers who integrate with your team and ship. No recruiting. No onboarding drag.
                  </p>
                </div>

                <div className="flex flex-col sm:flex-row gap-3 justify-center lg:justify-start">
                  <ServicePageCTA label="Book a Discovery Call" />
                </div>

                <p className="text-sm text-slate-500">
                  Monthly retainers or project-based. Flexible to your needs.
                </p>
              </SectionReveal>

              <SectionReveal delay={0.2} direction="right" className="w-full lg:flex-1 flex justify-center">
                <HeroTeamVelocity />
              </SectionReveal>
            </div>
          </div>
        </section>

        <section className="w-full bg-white py-12 md:py-16 lg:py-20">
          <div className="container px-4 md:px-6 lg:px-8">
            <SectionReveal className="max-w-4xl mx-auto">
              <div className="space-y-3 text-center">
                <p className="text-[#ed2132] font-semibold">WHO THIS IS FOR</p>
                <p className="text-lg text-slate-600">
                  Teams with backlogs and product roadmaps. Companies looking to scale engineering capacity without the 2-3 month hiring cycle. Budgets starting at $8k/month.
                </p>
              </div>
            </SectionReveal>
          </div>
        </section>

        <section className="w-full bg-slate-50 py-12 md:py-16 lg:py-20">
          <div className="container px-4 md:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto space-y-10">
              <SectionReveal className="space-y-4 text-center">
                <h2 className="text-3xl md:text-4xl font-bold text-slate-900">
                  Your Backlog Is Growing Faster Than Your Team
                </h2>
                <p className="text-lg text-slate-600 max-w-2xl mx-auto">
                  You need experienced developers. Hiring takes too long.
                </p>
              </SectionReveal>

              <StaggerReveal className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {[
                  {
                    icon: Clock,
                    title: "Hiring Takes 4-8 Weeks",
                    description: "Job posts, interviews, negotiations, onboarding. Meanwhile, your roadmap slips."
                  },
                  {
                    icon: Users,
                    title: "Junior Devs Need Handholding",
                    description: "You end up spending more time managing than building. That's not sustainable."
                  },
                  {
                    icon: Calendar,
                    title: "Deadlines Keep Moving",
                    description: "Important features get pushed. Customers wait. Competition moves faster."
                  },
                  {
                    icon: Zap,
                    title: "Context Switching Kills Productivity",
                    description: "Contractors come and go. Each new person means weeks of ramp-up time."
                  }
                ].map((problem, idx) => {
                  const Icon = problem.icon
                  return (
                    <StaggerItem key={idx}>
                      <div className="flex gap-4 bg-white border border-slate-200 rounded-lg p-6 hover:shadow-md hover:border-slate-300 transition-all duration-300">
                        <div className="flex-shrink-0">
                          <div className="flex items-center justify-center h-10 w-10 rounded-lg bg-red-100">
                            <Icon className="h-5 w-5 text-[#ed2132]" />
                          </div>
                        </div>
                        <div className="space-y-1">
                          <h3 className="text-lg font-semibold text-slate-900">{problem.title}</h3>
                          <p className="text-slate-600 text-sm">{problem.description}</p>
                        </div>
                      </div>
                    </StaggerItem>
                  )
                })}
              </StaggerReveal>
            </div>
          </div>
        </section>

        <section className="w-full bg-white py-12 md:py-16 lg:py-20">
          <div className="container px-4 md:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto space-y-10">
              <SectionReveal className="space-y-4 text-center">
                <p className="text-[#ed2132] font-semibold">THE SOLUTION</p>
                <h2 className="text-3xl md:text-4xl font-bold text-slate-900">
                  Experienced Developers, Ready to Ship
                </h2>
                <p className="text-lg text-slate-600 max-w-2xl mx-auto">
                  Senior engineers who integrate with your workflows and start contributing immediately.
                </p>
              </SectionReveal>

              <StaggerReveal className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {[
                  {
                    icon: Code,
                    title: "Full-Stack Expertise",
                    description: "React, Node, Python, Rails, and modern cloud infrastructure. We match skills to your stack."
                  },
                  {
                    icon: GitBranch,
                    title: "Your Workflow",
                    description: "We use your tools, follow your processes, and integrate with your existing team seamlessly."
                  },
                  {
                    icon: MessageSquare,
                    title: "Clear Communication",
                    description: "Async-first, timezone-flexible. Daily standups or weekly syncs, whatever works for you."
                  }
                ].map((solution, idx) => {
                  const Icon = solution.icon
                  return (
                    <StaggerItem key={idx}>
                      <div className="space-y-4 text-center group">
                        <div className="flex items-center justify-center h-12 w-12 rounded-lg bg-red-100 mx-auto transition-transform duration-300 group-hover:scale-110">
                          <Icon className="h-6 w-6 text-[#ed2132]" />
                        </div>
                        <h3 className="text-lg font-semibold text-slate-900">{solution.title}</h3>
                        <p className="text-slate-600 text-sm">{solution.description}</p>
                      </div>
                    </StaggerItem>
                  )
                })}
              </StaggerReveal>
            </div>
          </div>
        </section>

        <section className="w-full bg-slate-50 py-12 md:py-16 lg:py-20">
          <div className="container px-4 md:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto space-y-10">
              <SectionReveal className="space-y-4 text-center">
                <p className="text-[#ed2132] font-semibold">WHAT YOU GET</p>
                <h2 className="text-3xl md:text-4xl font-bold text-slate-900">
                  A Real Extension of Your Team
                </h2>
              </SectionReveal>

              <StaggerReveal className="grid grid-cols-1 md:grid-cols-2 gap-4" staggerDelay={0.06}>
                {[
                  "Senior full-stack developers (7+ years)",
                  "Direct Slack/Teams access",
                  "Code reviews and PR collaboration",
                  "Architecture and technical guidance",
                  "Documentation and knowledge transfer",
                  "Flexible hours (20-40/week options)",
                  "No long-term contracts required",
                  "Week-to-week flexibility"
                ].map((item, idx) => (
                  <StaggerItem key={idx}>
                    <div className="flex items-center gap-3 bg-white border border-slate-200 rounded-lg p-4 hover:shadow-md hover:border-slate-300 transition-all duration-300">
                      <div className="flex-shrink-0">
                        <Check className="h-5 w-5 text-[#ed2132]" />
                      </div>
                      <span className="text-slate-700">{item}</span>
                    </div>
                  </StaggerItem>
                ))}
              </StaggerReveal>
            </div>
          </div>
        </section>

        <section className="w-full bg-white py-12 md:py-16 lg:py-20">
          <div className="container px-4 md:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto space-y-10">
              <SectionReveal className="space-y-4 text-center">
                <p className="text-[#ed2132] font-semibold">HOW WE WORK</p>
                <h2 className="text-3xl md:text-4xl font-bold text-slate-900">
                  Integrate Fast, Deliver Faster
                </h2>
              </SectionReveal>

              <StaggerReveal className="grid grid-cols-1 md:grid-cols-4 gap-6" staggerDelay={0.1}>
                {[
                  {
                    step: "01",
                    title: "Kickoff",
                    description: "We learn your codebase, processes, and priorities. Usually 2-3 days."
                  },
                  {
                    step: "02",
                    title: "First PR",
                    description: "We're shipping code within the first week. Real work, not ramp-up exercises."
                  },
                  {
                    step: "03",
                    title: "Full Speed",
                    description: "By week 2-3, we're operating at full velocity on your highest priorities."
                  },
                  {
                    step: "04",
                    title: "Ongoing",
                    description: "Continuous delivery, code reviews, architecture input. A true team extension."
                  }
                ].map((phase, idx) => (
                  <StaggerItem key={idx}>
                    <div className="bg-red-50 border border-red-200 rounded-lg p-6 space-y-3 hover:shadow-lg hover:border-red-300 transition-all duration-300">
                      <p className="text-[#ed2132] text-sm font-bold">STEP {phase.step}</p>
                      <h3 className="text-lg font-bold text-slate-900">{phase.title}</h3>
                      <p className="text-slate-600 text-sm">{phase.description}</p>
                    </div>
                  </StaggerItem>
                ))}
              </StaggerReveal>
            </div>
          </div>
        </section>

        <section className="w-full bg-slate-50 py-12 md:py-16 lg:py-20">
          <div className="container px-4 md:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto space-y-10">
              <SectionReveal className="space-y-4 text-center">
                <p className="text-[#ed2132] font-semibold">ENGAGEMENT OPTIONS</p>
                <h2 className="text-3xl md:text-4xl font-bold text-slate-900">
                  Flexible to Your Needs
                </h2>
              </SectionReveal>

              <StaggerReveal className="grid grid-cols-1 md:grid-cols-2 gap-8" staggerDelay={0.15}>
                <StaggerItem>
                  <div className="bg-white border border-slate-200 rounded-xl p-8 space-y-6 hover:shadow-xl hover:border-slate-300 transition-all duration-300">
                    <div className="space-y-2">
                      <h3 className="text-xl font-bold text-slate-900">Monthly Retainer</h3>
                      <p className="text-slate-600 text-sm">Dedicated capacity, predictable cost</p>
                    </div>
                    <div className="space-y-4">
                      <div className="space-y-1">
                        <p className="text-3xl font-bold text-[#ed2132]">$8k-$12k/mo</p>
                        <p className="text-slate-500 text-sm">Per senior developer</p>
                      </div>
                      <ul className="space-y-2 text-sm text-slate-600">
                        <li className="flex items-start gap-2">
                          <Check className="h-4 w-4 text-[#ed2132] mt-0.5 flex-shrink-0" />
                          Full-time or part-time options
                        </li>
                        <li className="flex items-start gap-2">
                          <Check className="h-4 w-4 text-[#ed2132] mt-0.5 flex-shrink-0" />
                          Month-to-month, no long-term lock
                        </li>
                        <li className="flex items-start gap-2">
                          <Check className="h-4 w-4 text-[#ed2132] mt-0.5 flex-shrink-0" />
                          Dedicated to your projects
                        </li>
                      </ul>
                    </div>
                  </div>
                </StaggerItem>

                <StaggerItem>
                  <div className="bg-white border border-slate-200 rounded-xl p-8 space-y-6 hover:shadow-xl hover:border-slate-300 transition-all duration-300">
                    <div className="space-y-2">
                      <h3 className="text-xl font-bold text-slate-900">Project-Based</h3>
                      <p className="text-slate-600 text-sm">Fixed scope, fixed price</p>
                    </div>
                    <div className="space-y-4">
                      <div className="space-y-1">
                        <p className="text-3xl font-bold text-[#ed2132]">Custom Quote</p>
                        <p className="text-slate-500 text-sm">Based on scope and timeline</p>
                      </div>
                      <ul className="space-y-2 text-sm text-slate-600">
                        <li className="flex items-start gap-2">
                          <Check className="h-4 w-4 text-[#ed2132] mt-0.5 flex-shrink-0" />
                          Clear deliverables and milestones
                        </li>
                        <li className="flex items-start gap-2">
                          <Check className="h-4 w-4 text-[#ed2132] mt-0.5 flex-shrink-0" />
                          Good for one-time initiatives
                        </li>
                        <li className="flex items-start gap-2">
                          <Check className="h-4 w-4 text-[#ed2132] mt-0.5 flex-shrink-0" />
                          Can convert to retainer after
                        </li>
                      </ul>
                    </div>
                  </div>
                </StaggerItem>
              </StaggerReveal>
            </div>
          </div>
        </section>

        <ContactSection
          title="Ready to Scale Your Engineering Team?"
          description="Tell us about your backlog. We'll match senior developers to your stack and have them shipping code within the first week."
          inquiryPrompt="What kind of help do you need?"
          inquiryOptions={[
            { value: "one-dev", label: "One senior developer to add to my team" },
            { value: "small-team", label: "A small team to own a product area" },
            { value: "backlog", label: "Help clearing a growing backlog" },
            { value: "fractional-cto", label: "Technical leadership / fractional CTO" },
            { value: "other", label: "Other - please contact me to discuss" },
          ]}
          service="Dedicated Developers"
        />
      </main>
    </CompanyLayout>
  )
}
